/* eslint-disable camelcase, no-magic-numbers, require-jsdoc */
import { getParentNameFromSlug } from 'config/page-parents';
import getVideoId from 'get-video-id';
import { isPastEventDate } from './format';
import {
    ORGANIZATION_ADDRESS,
    ORGANIZATION_EMAIL,
    ORGANIZATION_PHONE,
    PARENT_ORGANIZATION,
    seoAlternateName,
    seoDomain,
    seoImagePath,
    siteTitle,
    SOCIAL_URLS
} from 'config/seo';

const LANGUAGE = 'en-NZ';
const LOGO_ID = `${seoDomain}/#logo`;
const MUSEUM_ID = `${seoDomain}/#museum`;
const ORGANIZATION_ID = `${seoDomain}/#organization`;
const ROOT_URL = `${seoDomain}/`;
const WEBSITE_ID = `${seoDomain}/#website`;

export function getCanonicalUrl(path = '') {
    const slug = String(path || '').replace(/^\/+/, '').replace(/\/+$/, '');

    return slug ? `${seoDomain}/${slug}` : ROOT_URL;
}

export function absoluteUrl(value) {
    if (!value) return undefined;
    if (Array.isArray(value)) return value.map((item) => absoluteUrl(item)).filter(Boolean);
    if (typeof value !== 'string') return value;
    if ((/^https?:\/\//i).test(value)) return value;
    if (value.startsWith('//')) return `https:${value}`;

    return value.startsWith('/') ? `${seoDomain}${value}` : `${seoDomain}/${value}`;
}

export function stripHtml(value = '') {
    if (value === null || value === undefined) return '';
    if ((/^(null|undefined)$/i).test(String(value).trim())) return '';

    return decodeEntities(String(value)
        .replace(/<script[\s\S]*?<\/script>/gi, ' ')
        .replace(/<style[\s\S]*?<\/style>/gi, ' ')
        .replace(/<[^>]+>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim());
}

export function getPageCanonicalUrl(page = {}) {
    const attributes = entityAttributes(page);
    const { parent, slug } = attributes;

    if (!slug || slug === 'home') return ROOT_URL;

    const segment = entitySegment(page.type);
    if (segment) return getCanonicalUrl(`${segment}/${slug}`);

    const parentSlug = resolveParentSlug(parent);
    if (parentSlug) return getCanonicalUrl(`${parentSlug}/${slug}`);

    return getCanonicalUrl(slug);
}

export function getWebsiteStructuredData() {
    return {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        '@id': WEBSITE_ID,
        url: seoDomain,
        name: siteTitle,
        alternateName: seoAlternateName,
        inLanguage: LANGUAGE,
        publisher: organizationRef()
    };
}

export function getOrganizationStructuredData() {
    return graph([
        {
            '@type': 'Organization',
            '@id': ORGANIZATION_ID,
            name: siteTitle,
            alternateName: seoAlternateName,
            url: seoDomain,
            logo: logoImageObject(),
            telephone: ORGANIZATION_PHONE,
            email: ORGANIZATION_EMAIL,
            address: ORGANIZATION_ADDRESS,
            parentOrganization: parentOrganizationRef(),
            sameAs: SOCIAL_URLS
        },
        PARENT_ORGANIZATION
    ]);
}

export function getMuseumStructuredData(globalFields = {}) {
    const opens = formatSchemaTime(globalFields && globalFields.open_time);
    const closes = formatSchemaTime(globalFields && globalFields.close_time);

    // Emitted as a standalone block, so the logo is inlined rather than
    // referenced by @id from the organisation block.
    return compact({
        '@context': 'https://schema.org',
        '@type': ['Museum', 'TouristAttraction'],
        '@id': MUSEUM_ID,
        name: siteTitle,
        alternateName: seoAlternateName,
        url: seoDomain,
        image: seoImagePath,
        telephone: ORGANIZATION_PHONE,
        address: ORGANIZATION_ADDRESS,
        ...(opens && closes ? {
            openingHoursSpecification: {
                '@type': 'OpeningHoursSpecification',
                dayOfWeek: [
                    'Monday',
                    'Tuesday',
                    'Wednesday',
                    'Thursday',
                    'Friday',
                    'Saturday',
                    'Sunday'
                ],
                opens,
                closes
            }
        } : {})
    });
}

export function getStructuredDataForPage(page = {}, options = {}) {
    const attributes = entityAttributes(page);
    const pageUrl = options.url || getPageCanonicalUrl(page);
    const pageTitle = options.title || attributes.seo_title || attributes.name || siteTitle;
    const pageDescription = stripHtml(
        options.description ||
        attributes.seo_description ||
        attributes.short_description ||
        attributes.description ||
        attributes.intro
    );
    const image = absoluteUrl(options.image || primaryImage(attributes));
    const specificNode = getSpecificPageNode(page, {
        description: pageDescription,
        image,
        title: pageTitle,
        url: pageUrl
    });
    const breadcrumbs = getBreadcrumbItems(page);

    return graph([
        compact({
            '@type': pageWebPageType(attributes.slug),
            '@id': `${pageUrl}#webpage`,
            url: pageUrl,
            name: pageTitle,
            description: pageDescription,
            image,
            inLanguage: LANGUAGE,
            // Every other entity is emitted as its own top-level node, so the page
            // deliberately does not reference the WebSite, Museum, or page entity.
            breadcrumb: breadcrumbs.length ? { '@id': `${pageUrl}#breadcrumb` } : undefined
        }),
        specificNode
    ]);
}

export function getBreadcrumbStructuredData(page = {}) {
    const pageUrl = getPageCanonicalUrl(page);
    const items = getBreadcrumbItems(page);
    if (!items.length) return null;

    return withContext({
        '@type': 'BreadcrumbList',
        '@id': `${pageUrl}#breadcrumb`,
        itemListElement: items
    });
}

export function getContentBlocksStructuredData(blocks = [], page = {}) {
    const url = absoluteUrl(page.url) || ROOT_URL;
    const nodes = [];
    const faqQuestions = [];

    (Array.isArray(blocks) ? blocks : []).filter(isVisible).forEach((block, index) => {
        if (block.type === 'faq') {
            faqQuestions.push(...faqQuestionsFromBlock(block));

            return;
        }
        if (block.type === 'video') {
            nodes.push(video(block, index, url, page.title));
        }
    });

    if (faqQuestions.length) {
        nodes.unshift(compact({
            '@type': 'FAQPage',
            '@id': `${url}#faq`,
            url,
            name: page.title,
            inLanguage: LANGUAGE,
            mainEntity: faqQuestions
        }));
    }

    return graph(nodes);
}

function getSpecificPageNode(page, options) {
    const attributes = entityAttributes(page);
    const type = page.type;

    if (isEntityType(type, 'events', 'event')) {
        return eventNode(attributes, options);
    }
    if (isEntityType(type, 'exhibitions', 'exhibition') &&
        validDate(attributes.start_date) &&
        validDate(attributes.end_date)) {
        return eventNode(attributes, options, true);
    }
    if (isEntityType(type, 'sailings', 'sailing')) {
        return sailingServiceNode(attributes, options);
    }
    if (isEntityType(type, 'stories', 'story')) {
        return articleNode(attributes, options);
    }
    if (isEntityType(type, 'educations', 'education')) {
        return courseNode(attributes, options);
    }

    return null;
}

function eventNode(attributes, options, isExhibition = false) {
    const idSuffix = isExhibition ? 'exhibitionevent' : 'event';
    const startDate = validDate(attributes.start_date);
    const endDate = validDate(attributes.end_date);
    const location = eventLocation(attributes);
    if (!location) return null;

    // Finished events are no longer offered, so they get no event markup at all. This
    // uses the same rule as the "Past Event" page title, keeping the two consistent.
    if (isPastEventDate(attributes.end_date || attributes.start_date)) return null;

    return compact({
        '@type': isExhibition ? 'ExhibitionEvent' : 'Event',
        '@id': `${options.url}#${idSuffix}`,
        name: attributes.name || options.title,
        url: options.url,
        description: options.description,
        image: images(options.image),
        startDate,
        endDate,
        eventStatus: eventStatus(attributes, startDate),
        eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
        location,
        organizer: organizationRef(),
        offers: eventOffer(attributes, options.url)
    });
}

function sailingServiceNode(attributes, options) {
    return compact({
        '@type': 'Service',
        '@id': `${options.url}#service`,
        name: attributes.name || options.title,
        url: options.url,
        description: options.description,
        image: images(options.image),
        serviceType: 'Heritage sailing experience',
        provider: organizationRef(),
        offers: offerFromPrice({
            description: attributes.price_summary,
            id: `${options.url}#offer`,
            price: attributes.price,
            seller: museumRef(),
            url: absoluteUrl(attributes.tickets_link) || options.url
        })
    });
}

function courseNode(attributes, options) {
    return compact({
        '@type': 'Course',
        '@id': `${options.url}#course`,
        name: attributes.name || options.title,
        url: options.url,
        description: options.description,
        image: images(options.image),
        provider: organizationRef(),
        educationalLevel: attributes.level,
        audience: attributes.level && {
            '@type': 'EducationalAudience',
            audienceType: attributes.level
        },
        offers: offerFromPrice({
            id: `${options.url}#offer`,
            price: attributes.cost,
            seller: museumRef(),
            url: absoluteUrl(attributes.booking_link || attributes.tickets_link) || options.url
        })
    });
}

function articleNode(attributes, options) {
    return compact({
        '@type': 'BlogPosting',
        '@id': `${options.url}#article`,
        headline: attributes.name || options.title,
        url: options.url,
        description: options.description,
        image: images(options.image),
        author: attributes.author && {
            '@type': 'Person',
            name: stripHtml(attributes.author)
        },
        datePublished: validDate(attributes.publish_at || attributes.published_at || attributes.created_at),
        dateModified: validDate(
            attributes.updated_at ||
            attributes.modified_at ||
            attributes.publish_at ||
            attributes.published_at
        ),
        publisher: organizationRef(),
        inLanguage: LANGUAGE
    });
}

function eventLocation(attributes = {}) {
    const name = stripHtml(attributes.location);
    const address = stripHtml(attributes.location_physical_address);

    if (name && (/maritime museum/i).test(name)) return museumRef();
    if (name || address) {
        return compact({
            '@type': 'Place',
            name,
            address: address && {
                '@type': 'PostalAddress',
                streetAddress: address
            }
        });
    }
    if (visibleEventLocationText(attributes.blocks)) return museumRef();

    return undefined;
}

function visibleEventLocationText(blocks = []) {
    const text = (Array.isArray(blocks) ? blocks : [])
        .filter(isVisible)
        .filter((block) => block.type !== 'contentset')
        .map((block) => {
            const data = block.data || {};
            if (Array.isArray(data)) {
                return data.filter(isVisible)
                    .map((item) => `${item.title || ''} ${item.content || ''} ${item.aside || ''}`)
                    .join(' ');
            }

            return `${data.title || ''} ${data.content || ''} ${data.aside || ''} ${data.subtitle || ''}`;
        })
        .join(' ');

    return (/(?:new zealand|nz)?\s*maritime museum|\bmain concourse\b|\bthe concourse\b|\bin the marina\b/i)
        .test(stripHtml(text));
}

function eventOffer(attributes = {}, url) {
    const offerUrl = absoluteUrl(attributes.tickets_link);
    const price = attributes.start_price !== undefined && attributes.start_price !== null ?
        attributes.start_price :
        attributes.price || attributes.price_summary;
    if (!offerUrl && price === undefined || isPastEventDate(attributes.end_date || attributes.start_date)) return undefined;

    return offerFromPrice({
        allowWithoutPrice: Boolean(offerUrl),
        description: attributes.price_summary,
        highPrice: attributes.end_price,
        id: `${url}#offer`,
        price,
        seller: organizationRef(),
        url: offerUrl || url
    });
}

function offerFromPrice({
    allowWithoutPrice = false,
    description,
    highPrice,
    id,
    price,
    seller,
    url
} = {}) {
    const parsed = priceValues(price).slice().sort((low, high) => low - high);
    const explicitHigh = numericPrice(highPrice);
    const low = parsed[0];
    const high = explicitHigh === undefined ? parsed[parsed.length - 1] : explicitHigh;
    const isRange = low !== undefined && high !== undefined && low !== high;
    if (low === undefined && high === undefined && !allowWithoutPrice) return undefined;

    return compact({
        '@type': isRange ? 'AggregateOffer' : 'Offer',
        '@id': id,
        url,
        description: stripHtml(description),
        priceCurrency: low !== undefined || high !== undefined ? 'NZD' : undefined,
        price: !isRange && (low === undefined ? high : low) !== undefined ?
            (low === undefined ? high : low).toFixed(2) :
            undefined,
        lowPrice: isRange ? low.toFixed(2) : undefined,
        highPrice: isRange ? high.toFixed(2) : undefined,
        seller
    });
}

function priceValues(value) {
    if (value === undefined || value === null || value === '') return [];
    if (typeof value === 'number') return Number.isNaN(value) ? [] : [value];

    const text = String(value);
    if ((/\bfree\b/i).test(text)) return [0];

    // Only treat digits after a currency marker as prices so age ranges like
    // "Children (5 - 14 years) $12" do not become offer amounts.
    const currencyAmounts = Array.from(text.matchAll(/\$\s*([0-9]+(?:\.[0-9]{1,2})?)/g))
        .map((match) => Number(match[1]))
        .filter((item) => !Number.isNaN(item));
    if (currencyAmounts.length) return currencyAmounts;
    if ((/[a-z]/i).test(text)) return [];

    return (text.match(/[0-9]+(?:\.[0-9]{1,2})?/g) || [])
        .map((item) => Number(item))
        .filter((item) => !Number.isNaN(item));
}

function numericPrice(value) {
    return priceValues(value)[0];
}

function eventStatus(attributes, startDate) {
    const status = String(attributes.event_status || attributes.status || '').toLowerCase();
    const statuses = {
        cancelled: 'https://schema.org/EventCancelled',
        cancelled_event: 'https://schema.org/EventCancelled',
        postponed: 'https://schema.org/EventPostponed',
        rescheduled: 'https://schema.org/EventRescheduled',
        moved_online: 'https://schema.org/EventMovedOnline',
        scheduled: 'https://schema.org/EventScheduled'
    };
    if (statuses[status]) return statuses[status];
    if (startDate) return 'https://schema.org/EventScheduled';

    return undefined;
}

function getBreadcrumbItems(page = {}) {
    const attributes = entityAttributes(page);
    const { name, slug } = attributes;
    if (!slug || slug === 'home') return [];
    // The collections landing page is a signpost to the externally hosted
    // Online Collections site, so it is left out of the breadcrumb hierarchy.
    if (slug === 'collections' && !resolveParentSlug(attributes.parent)) return [];

    const items = [listItem(1, 'Home', ROOT_URL)];
    const segment = entitySegment(page.type);
    if (segment) {
        const labels = {
            educations: 'Learn',
            events: 'Events',
            exhibitions: 'Exhibitions',
            sailings: 'Sailings',
            stories: 'Stories and blogs'
        };
        const listingPaths = {
            educations: 'learn',
            events: 'whats-on/events',
            exhibitions: 'whats-on/exhibitions',
            stories: 'collections/stories-and-blogs'
        };

        return items.concat(
            listItem(2, labels[segment], getCanonicalUrl(listingPaths[segment] || segment)),
            listItem(3, name, getCanonicalUrl(`${segment}/${slug}`))
        );
    }

    const parentSlug = resolveParentSlug(attributes.parent);
    if (parentSlug) {
        return items.concat(
            listItem(2, getParentNameFromSlug(parentSlug), getCanonicalUrl(parentSlug)),
            listItem(3, name, getCanonicalUrl(`${parentSlug}/${slug}`))
        );
    }

    return items.concat(listItem(2, name, getCanonicalUrl(slug)));
}

function listItem(position, name, item) {
    return compact({
        '@type': 'ListItem',
        position,
        name,
        item
    });
}

function faqQuestionsFromBlock(block) {
    return (Array.isArray(block.data) ? block.data : [])
        .filter(isVisible)
        .filter((item) => item.title && item.content)
        .map((item) => ({
            '@type': 'Question',
            name: stripHtml(item.title),
            acceptedAnswer: {
                '@type': 'Answer',
                text: stripHtml(item.content)
            }
        }));
}

function video(block, index, url, title) {
    const data = block.data || {};
    const parsed = getVideoId(data.video_url || '');
    const embedUrl = videoEmbedUrl(parsed);

    return data.title && embedUrl && compact({
        '@type': 'VideoObject',
        '@id': `${url}#video-${index + 1}`,
        name: stripHtml(data.title),
        description: stripHtml(data.description || data.credit || title),
        embedUrl,
        contentUrl: absoluteUrl(data.content_url),
        thumbnailUrl: absoluteUrl(data.thumbnail_url) ||
            (parsed.service === 'youtube' ?
                `https://img.youtube.com/vi/${parsed.id}/hqdefault.jpg` :
                undefined),
        uploadDate: validDate(data.upload_date),
        publisher: organizationRef(),
        inLanguage: LANGUAGE
    });
}

function videoEmbedUrl(videoData) {
    if (!videoData || !videoData.id) return undefined;
    if (videoData.service === 'youtube') return `https://www.youtube.com/embed/${videoData.id}`;
    if (videoData.service === 'vimeo') return `https://player.vimeo.com/video/${videoData.id}`;

    return undefined;
}

function pageWebPageType(slug) {
    if (slug === 'contact-us') return 'ContactPage';
    if (slug === 'about' || String(slug || '').startsWith('about')) return 'AboutPage';

    return 'WebPage';
}

function entitySegment(type) {
    const segments = {
        education: 'educations',
        educations: 'educations',
        event: 'events',
        events: 'events',
        exhibition: 'exhibitions',
        exhibitions: 'exhibitions',
        sailing: 'sailings',
        sailings: 'sailings',
        stories: 'stories',
        story: 'stories'
    };

    return segments[type];
}

function primaryImage(attributes = {}) {
    return attributes.main_images && attributes.main_images[0] ||
        attributes.thumbnail_image ||
        attributes.landscape_thumbnail ||
        attributes.thumbnail;
}

function images(value) {
    const image = absoluteUrl(value);

    return Array.isArray(image) ? image : image && [image];
}

function logoImageObject() {
    return {
        '@type': 'ImageObject',
        '@id': LOGO_ID,
        url: seoImagePath
    };
}

function organizationRef() {
    return { '@id': ORGANIZATION_ID };
}

function museumRef() {
    return { '@id': MUSEUM_ID };
}

function parentOrganizationRef() {
    return { '@id': PARENT_ORGANIZATION['@id'] };
}

function validDate(value) {
    if (value === undefined || value === null || value === '') return undefined;
    if (typeof value === 'number' || (/^\d+$/).test(String(value))) {
        const number = Number(value);
        const milliseconds = number < 1000000000000 ? number * 1000 : number;
        const date = new Date(milliseconds);

        return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
    }

    return Number.isNaN(Date.parse(value)) ? undefined : value;
}

function formatSchemaTime(time) {
    if (!time) return undefined;

    return String(time).split(':').slice(0, 2).join(':');
}

function entityAttributes(page = {}) {
    return page.attributes || page || {};
}

function resolveParentSlug(parent) {
    if (!parent) return '';
    if (typeof parent === 'string') return parent;
    if (parent.slug) return parent.slug;
    if (parent.attributes && parent.attributes.slug) return parent.attributes.slug;

    return '';
}

function isEntityType(type, ...candidates) {
    return candidates.includes(type);
}

function isVisible(item = {}) {
    return !item.hidden && !item.disabled && item.is_published !== false && item.is_visible !== false;
}

function withContext(schema) {
    return compact({ '@context': 'https://schema.org', ...schema });
}

function graph(nodes) {
    const ids = {};
    const data = compact((Array.isArray(nodes) ? nodes : []).filter(Boolean))
        .filter((node) => {
            if (!node['@id']) return true;
            if (ids[node['@id']]) return false;
            ids[node['@id']] = true;

            return true;
        });

    return data && data.length ? { '@context': 'https://schema.org', '@graph': data } : null;
}

function compact(value) {
    if (Array.isArray(value)) return value.map((item) => compact(item)).filter((item) => !empty(item));
    if (value && typeof value === 'object') {
        return Object.keys(value).reduce((result, key) => {
            const item = compact(value[key]);
            if (!empty(item)) result[key] = item;

            return result;
        }, {});
    }

    return value;
}

function empty(value) {
    return value === undefined ||
        value === null ||
        value === '' ||
        Array.isArray(value) && !value.length ||
        Boolean(value && typeof value === 'object' && !Array.isArray(value) && !Object.keys(value).length);
}

function decodeEntities(value) {
    return String(value).replace(
        /&(#x[0-9a-fA-F]+|#\d+|[a-zA-Z][a-zA-Z0-9]+);/g,
        (match, entity) => {
            if (entity[0] === '#') {
                const codePoint = entity[1] === 'x' || entity[1] === 'X' ?
                    parseInt(entity.slice(2), 16) :
                    parseInt(entity.slice(1), 10);
                if (Number.isNaN(codePoint)) return match;

                try {
                    return String.fromCodePoint(codePoint);
                } catch (error) {
                    return match;
                }
            }

            const named = {
                amp: '&',
                apos: '\'',
                Eacute: 'É',
                eacute: 'é',
                gt: '>',
                hellip: '\u2026',
                ldquo: '\u201c',
                lsquo: '\u2018',
                lt: '<',
                mdash: '\u2014',
                nbsp: ' ',
                ndash: '\u2013',
                quot: '"',
                rdquo: '\u201d',
                rsquo: '\u2019'
            };

            return Object.prototype.hasOwnProperty.call(named, entity) ? named[entity] : match;
        }
    );
}
